import * as React from "react";
import { Redirect, Route, RouteProps, useLocation } from "react-router-dom";
import { RefreshCw } from "Components/ui/icons";

import { useAuth } from "../hooks/useAuth";
import { Button, ErrorState, LogoMark, Spinner } from "Components/ui";
import Forbidden from "Routes/System/Forbidden";

type Action = "read" | "write" | "delete" | "configure" | "approve";

interface ProtectedRouteProps extends RouteProps {
  requiredPermission?: { resource: string; action: Action };
  requiredAnyPermissions?: Array<{ resource: string; action: Action }>;
}

const FullPage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex min-h-[70vh] w-full flex-col items-center justify-center gap-4 px-6">
    {children}
  </div>
);

/**
 * Renders the route only for a signed-in user with the required permission. While the profile
 * loads it shows a loader (so a reload keeps the user on the page); signed-out users go to sign in
 * and come back here afterwards.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  requiredPermission,
  requiredAnyPermissions,
  ...rest
}) => {
  const { status, hasPermission, refresh } = useAuth();
  const location = useLocation();

  if (status === "anonymous") {
    return <Redirect to={{ pathname: "/credentials", state: { from: location } }} />;
  }

  if (status === "loading") {
    return (
      <FullPage>
        <LogoMark className="h-8 opacity-90" />
        <Spinner className="h-5 w-5 text-muted-foreground" label="Loading your session" />
      </FullPage>
    );
  }

  if (status === "error") {
    return (
      <FullPage>
        <ErrorState
          title="Can't reach SAP"
          description="Your session is still valid, but the server didn't respond. Check your connection and try again."
          action={
            <Button variant="secondary" leftIcon={<RefreshCw />} onClick={() => refresh()}>
              Try again
            </Button>
          }
        />
      </FullPage>
    );
  }

  const allowed = requiredAnyPermissions?.length
    ? requiredAnyPermissions.some((p) => hasPermission(p.resource, p.action))
    : !requiredPermission || hasPermission(requiredPermission.resource, requiredPermission.action);

  if (!allowed) {
    return <Forbidden />;
  }

  return <Route {...rest} />;
};

export default ProtectedRoute;
